/**
 * What applying a batch of spreadsheet ops returns: the ONE contract the
 * backend change-list core produces, the `apply_spreadsheet_ops` MCP tool
 * returns as structured content, and the chat change card renders. The card is
 * built only from this, so everything it shows is here.
 *
 * `summary`, `detail` and `problem` are written by CODE from the op and its
 * read-back, never by the model, and never hold a cell value or formula. They
 * may name a sheet, a range, a number format pattern or a chart type: this
 * result goes to the model and to the user's own UI, never to logs, audit
 * metadata or analytics.
 *
 * Nothing rolls back on its own: an op reported `landed` stays in the
 * workbook even when the batch failed. A whole batch can be undone only when
 * the result carries an `undoToken` (`./undo.ts`): by the user with Undo on
 * the change card, or by the agent's `undo_spreadsheet_change` when the user
 * asks in chat.
 */
import { z } from 'zod';
import { SPREADSHEET_OP_NAMES } from './ops';
/**
 * - `landed`: applied, read back as sent when it ran, and saved (re-read after
 *   saving unless a later op in the batch changed the same thing).
 * - `failed`: applied or attempted, and the read-back or Univer said no. It may
 *   be partly in the workbook; `problem` says what was found.
 * - `not_attempted`: an earlier op failed, so this one never ran.
 * - `unknown`: the session stopped (timeout, save not confirmed) after this op
 *   ran or while it ran, so whether it was saved is not known.
 */
export const SPREADSHEET_CHANGE_OUTCOMES = [
    'landed',
    'failed',
    'not_attempted',
    'unknown',
];
/**
 * Reasons the backend raises BEFORE the op mutates anything (the target was
 * checked and refused), so a change failed for one of these is not in the
 * sheet. The backend summary and the change card headline both read this one
 * list to say "No changes were made"; every other reason may leave part of the
 * change behind.
 */
export const SPREADSHEET_REFUSED_REASONS = [
    'sheet_not_found',
    'sheet_name_taken',
    'range_unbounded',
    'sort_range_has_formulas',
];
/**
 * Why a change failed or is unknown, for CODE: the MCP tool maps it to the
 * model's next step, while `problem` stays a plain statement for the user.
 */
export const SPREADSHEET_CHANGE_REASONS = [
    ...SPREADSHEET_REFUSED_REASONS,
    'text_reinterpreted',
    'formula_error',
    'read_back_mismatch',
    'engine_error',
    'not_saved',
];
// `z.enum` needs a non-empty tuple; the list is derived from the op union, so
// it has every op and at least one.
const opNameSchema = z.enum(SPREADSHEET_OP_NAMES);
export const spreadsheetChangeSchema = z
    .object({
    /** Position in the batch as sent, zero-based. */
    index: z.number().int().nonnegative(),
    op: opNameSchema,
    /** The sheet the op acts on (the new name for `addSheet`); null for none. */
    sheet: z.string().nullable(),
    /** A1 range as sent; null for ops without one (`clearFilter`, sheet ops). */
    range: z.string().nullable(),
    /** What the op does, e.g. "Sorted by column C, descending". */
    detail: z.string(),
    outcome: z.enum(SPREADSHEET_CHANGE_OUTCOMES),
    /**
     * Why it failed or is unknown, as a plain statement for the user (never
     * an instruction to the model); absent when it landed or was not attempted.
     */
    problem: z.string().optional(),
    /** The same, as a code for the tool; present exactly when `problem` is. */
    reason: z.enum(SPREADSHEET_CHANGE_REASONS).optional(),
    /**
     * FOR THE MODEL ONLY: what the read-back found, as addresses, counts and
     * error codes (e.g. "1 cells evaluate to an error: E2 #DIV/0!"), never a
     * value or formula, so the model can name the cell. The MCP tool puts it
     * in its failure message and removes it before the change card sees it.
     */
    found: z.string().optional(),
})
    .strict();
export const applySpreadsheetOpsResultSchema = z
    .object({
    /** `applied` only when EVERY op landed. Anything else is `failed`. */
    status: z.enum(['applied', 'failed']),
    artifactId: z.string(),
    summary: z.string(),
    /** One per op, in batch order. */
    changes: z.array(spreadsheetChangeSchema),
    /**
     * The unit's revision as the headless client held it after loading and
     * after saving. Local revisions, not a server query. Measured against a
     * local universer 1.0.2 on 2026-09-27, `revAfter` advanced past
     * `revBefore` after a real synced write (1 to 2; 2 to 5 for a 10-op
     * batch); phase 3 undo must still confirm it against the server head at
     * undo time. Absent when not read (the session stopped first).
     */
    revBefore: z.number().int().optional(),
    revAfter: z.number().int().optional(),
    /**
     * Opaque, signed by the backend. Present only when this batch changed the
     * workbook. Reverses THIS batch for THIS user: `POST
     * /api/spreadsheets/:artifactId/agent-undo` with this token as the body.
     * Never written to app logs, audit metadata or analytics. It does reach
     * the model and its traces as part of the tool result.
     */
    undoToken: z.string().min(1).optional(),
})
    .strict();
//# sourceMappingURL=result.js.map